import {
    useMemo,
    useState,
} from 'react'
import noop from 'lodash/noop'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    ForwarderCategory,
} from 'app/enums/label.enums'
import {
    RequestConfig,
} from 'app/types/request.types'
import FormWithControls from 'app/shared-components/FormWithControls'

import {
    FreightForwarder,
    ImportInstructions,
} from '../FreightForwarders.types'
import getFreightForwarderCreateFields from './FreightForwarderForm.fields'
import useStyles from './FreightForwarderForm.style'

type FreightForwarderFormProps = {
    data?: FreightForwarder,
    onSuccess?: (...args: any[]) => void,
    onCancel?: (...args: any[]) => void,
    request?: RequestConfig,
    disabled?: boolean,
}

const defaultProps: Partial<FreightForwarderFormProps> = {
    data: {} as FreightForwarder,
    onSuccess: noop,
    onCancel: noop,
    request: undefined,
    disabled: false,
}

const FreightForwarderForm = ({
    data,
    onSuccess,
    onCancel,
    request,
    disabled,
}: FreightForwarderFormProps) => {
    const exists = !!data?.id
    const getAllowedValue = useEnumValues()
    const {
        classes,
    } = useStyles()

    // Monitor fields changes to show/hide import informations
    // depending on the forwarder categories selected by the user
    //
    const [
        formValue,
        setFormValue,
    ] = useState<FreightForwarder>({
        ...data,
        ...data?.importInstructions,
    })

    const showImportInstructions = useMemo(() => {
        return (formValue.forwarderCategory || []).some((category) => {
            return [
                ForwarderCategory.IMPORT_SEA,
                ForwarderCategory.IMPORT_AIR,
            ].includes(category)
        })
    }, [formValue?.forwarderCategory])

    const fields = useMemo(() => {
        return getFreightForwarderCreateFields(getAllowedValue, showImportInstructions)
    }, [
        getAllowedValue,
        showImportInstructions,
    ])

    // Remap data before request to have the import information
    // as a nested object
    //
    const formDataToRequestBody = (formContent) => {
        const {
            masterConsignee,
            masterAdditionalInfo,
            houseConsignee,
            houseAdditionalInfo,
            // Deconstruct the embedded importInstructions object initially received from the
            // backend to make sure we don't include it.
            // The updated values are in masterConsignee, etc.
            //  eslint-disable-next-line @typescript-eslint/no-unused-vars
            importInstructions: unflattenedImportInstructions,
            ...rest
        } = formContent

        // masterConsignee is a mandatory field in ImportInstructions
        if (showImportInstructions && masterConsignee) {
            const importInstructions: ImportInstructions = {
                masterConsignee,
            }

            if (masterAdditionalInfo) importInstructions.masterAdditionalInfo = masterAdditionalInfo
            if (houseConsignee) importInstructions.houseConsignee = houseConsignee
            if (houseAdditionalInfo) importInstructions.houseAdditionalInfo = houseAdditionalInfo

            return {
                ...rest,
                importInstructions,
            }
        }

        return {
            ...rest,
        }
    }

    return (
        <FormWithControls
            classNames={{
                gridWrapper: classes.gridWrapper,
            }}
            onSuccess={onSuccess}
            onCancel={onCancel}
            modifyDataBeforeSend={formDataToRequestBody}
            name="FreightForwarderForm"
            fields={fields}
            requestParams={request}
            value={formValue}
            setExternalValue={setFormValue}
            exists={exists}
            disabled={disabled}
        />
    )
}

FreightForwarderForm.defaultProps = defaultProps

export default FreightForwarderForm
